// src/components/Sidebar/ComponentLibrary/SidebarDragPreview.tsx
// Shows a floating icon preview that follows the cursor during drag from sidebar

import React, { useEffect, useState, useRef } from 'react';
import { useUiStateStore } from '@/stores/uiStateStore';
import { iconPackManager } from '@/features/components/iconPacks';
import { IconRenderer } from '@/features/components/iconRenderer';

interface MousePosition {
  x: number;
  y: number;
}

export const SidebarDragPreview: React.FC = () => {
  const mode = useUiStateStore((state) => state.mode);
  const uiStateActions = useUiStateStore((state) => state.actions);
  const rendererEl = useUiStateStore((state) => state.rendererEl);
  const [mousePos, setMousePos] = useState<MousePosition | null>(null);
  const [isOverCanvas, setIsOverCanvas] = useState(false);
  const isOverCanvasRef = useRef(false);

  // Only show when in PLACE_ICON mode with an icon selected
  const isActive = mode.type === 'PLACE_ICON' && !!mode.id;
  const iconId = isActive ? mode.id : null;

  // Get icon definition
  const iconDef = iconId ? iconPackManager.getIconById(iconId) : null;

  // Track global mouse position when active
  useEffect(() => {
    if (!isActive) {
      setMousePos(null);
      setIsOverCanvas(false);
      isOverCanvasRef.current = false;
      return;
    }

    // Set grabbing cursor globally during drag
    const originalCursor = document.body.style.cursor;
    document.body.style.cursor = 'grabbing';

    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });

      // Check if mouse is over the canvas
      if (rendererEl) {
        const rect = rendererEl.getBoundingClientRect();
        const isInside = (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        );
        setIsOverCanvas(isInside);
        isOverCanvasRef.current = isInside;

        // Change cursor to crosshair when over canvas
        document.body.style.cursor = isInside ? 'crosshair' : 'grabbing';
      }
    };

    // Handle mouseup - reset cursor and cancel if outside canvas
    const handleMouseUp = () => {
      document.body.style.cursor = originalCursor || 'default';

      // If mouse is NOT over canvas when released, cancel placement
      if (!isOverCanvasRef.current) {
        uiStateActions.setMode({
          type: 'CURSOR',
          showCursor: true,
          mousedownItem: null
        });
      }
      // If over canvas, the canvas interaction manager will handle placement
    };

    // Track mouse globally
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = originalCursor || 'default';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isActive, rendererEl, uiStateActions]);

  // Don't render if not active, no position, or mouse is over canvas
  // (when over canvas, the IconPlacementPreview handles the preview)
  if (!isActive || !mousePos || !iconDef || isOverCanvas) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: mousePos.x,
        top: mousePos.y,
        transform: 'translate(-50%, -100%)', // Center horizontally, position above cursor
        pointerEvents: 'none',
        zIndex: 10000, // Above everything
        opacity: 0.85,
        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))'
      }}
    >
      <div style={{ width: 48, height: 48 }}>
        <IconRenderer icon={iconDef} size={48} />
      </div>
    </div>
  );
};
